import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useOnError } from 'src/utils/errors';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { ServicePlanItem } from 'src/features/servicePlanItems/utils/types';
import { useActivateServicePlanItem } from 'src/features/servicePlanItems/api/useActivateServicePlanItem';
import { SERVICE_PLAN_ITEM_RESOURCE } from 'src/features/servicePlanItems/api/useGetServicePlanItem';
import { LIST_SERVICE_PLAN_ITEMS_RESOURCE } from 'src/features/servicePlanItems/api/useListServicePlanItems';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';

interface ActivateServicePlanItemFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanItem: ServicePlanItem;
    children?: React.ReactNode;
}

function ActivateServicePlanItemFormModal<TData>({
    selectedServicePlanItem,
    children,
}: ActivateServicePlanItemFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const form = useForm({
        resolver: zodResolver(z.object({})),
    });

    const { mutate: activateServicePlanItem } = useActivateServicePlanItem({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [SERVICE_PLAN_ITEM_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLAN_ITEMS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(servicePlanItemsMessages.activateServicePlanItemSuccess),
                description: formatMessage(servicePlanItemsMessages.activateServicePlanItemSuccessDescription),
            });
        },
        onError: (error) => onError(error, form.setError),
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...servicePlanItemsMessages.activateServicePlanItem },
                subTitle: {
                    ...servicePlanItemsMessages.activateServicePlanItemSubtitle,
                    values: {
                        name: selectedServicePlanItem.name,
                    },
                },
                icon: ICON_MAP.TOGGLE_ON,
                confirmationMessage: { ...servicePlanItemsMessages.confirmActivateServicePlanItem },
            }}
            callbackButton={{
                variant: 'default',
                buttonTitle: servicePlansMessages.activate,
                callbackFn: () => {
                    return (
                        activateServicePlanItem({
                            planItemId: selectedServicePlanItem.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default ActivateServicePlanItemFormModal;
